import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  RedoOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Empty, Spin, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { type WorkflowTemplate, type WorkflowVersionSummary, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import { VersionChip } from '@/components/VersionChip';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { CreateWorkflowModal } from './CreateWorkflowModal';
import { getSubmitReadiness } from './submitGuard';

const { Text } = Typography;

const OUTER_WRAP: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  overflow: 'hidden',
};

const TOP_BAR: React.CSSProperties = {
  background: '#f6f7f8',
  borderBottom: '1px solid #e5e7eb',
  padding: '14px 20px',
  minHeight: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export function VersionHistoryPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useRoles();
  // Approver entry: same page, same data, but every mutation control (Restore /
  // Create New Version / Edit / Submit / Cancel) is hidden. The route guard at
  // /approver/workflows/:id/versions enforces this server-side too, so the
  // read-only path here is the only path Approvers ever hit.
  const isApprover = role === 'Approver';
  const dashboardPath = isApprover ? '/approver/dashboard' : '/originator/dashboard';
  const [workflow, setWorkflow] = useState<WorkflowTemplate | null>(null);
  const [versions, setVersions] = useState<WorkflowVersionSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    if (!id) return;
    try {
      const [w, vs] = await Promise.all([workflowsApi.get(id), workflowsApi.listVersions(id)]);
      setWorkflow(w);
      // Defensive: BE always returns [] for templates with no versions yet, but guard
      // against the response shape changing or an edge path returning undefined so we
      // never crash with "vs.sort is not a function" on the version-history screen.
      const list = Array.isArray(vs) ? [...vs] : [];
      setVersions(list.sort((a, b) => a.versionNumber - b.versionNumber));
    } catch (err) {
      handleApiError(err, t, t('workflow.versions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // sourceVersionNumber === undefined means "fork from the live published row" (BE default).
  // Passing it explicitly forks from an archived row instead, and tags the new draft so
  // the table can show a "based on vN" lineage caption.
  const handleCreateNewVersion = async (sourceVersionNumber?: number) => {
    if (!workflow) return;
    setBusyRow(sourceVersionNumber ?? -1);
    try {
      await workflowsApi.startNewVersion(workflow.id, sourceVersionNumber);
      message.success(t('workflow.versions.newVersionDraftCreated'));
      void load();
    } catch (err) {
      handleApiError(err, t, t('workflow.versions.newVersionFailed'));
    } finally {
      setBusyRow(null);
    }
  };

  const handleSubmitDraft = async (row: WorkflowVersionSummary) => {
    if (!workflow) return;
    setBusyRow(row.versionNumber);
    try {
      await workflowsApi.submit(workflow.id);
      message.success(t('workflow.list.sentToApprover'));
      void load();
    } catch (err) {
      handleApiError(err, t, t('workflow.list.submitFailed'));
    } finally {
      setBusyRow(null);
    }
  };

  const handleRestoreArchived = async (row: WorkflowVersionSummary) => {
    if (!workflow) return;
    setBusyRow(row.versionNumber);
    try {
      await workflowsApi.activateVersion(workflow.id, row.versionNumber);
      message.success(t('workflow.versions.activatedSentForApproval'));
      void load();
    } catch (err) {
      handleApiError(err, t, t('workflow.versions.activateFailed'));
    } finally {
      setBusyRow(null);
    }
  };

  const handleDeleteDraft = async (row: WorkflowVersionSummary) => {
    if (!workflow) return;
    setBusyRow(row.versionNumber);
    try {
      await workflowsApi.cancelNewVersion(workflow.id);
      message.success(t('workflow.versions.newVersionCanceled'));
      void load();
    } catch (err) {
      handleApiError(err, t, t('workflow.versions.newVersionCancelFailed'));
    } finally {
      setBusyRow(null);
    }
  };

  const renderRowActions = (row: WorkflowVersionSummary) => {
    if (!workflow) return null;

    // Approver view is strictly read-only — every row's eye opens the details +
    // configuration of THAT specific version's frozen snapshot. Rendering the
    // frozen version (not the template's CURRENT state) is what stops v2's edits
    // from leaking into v1's view after a new version is published.
    if (isApprover) {
      return (
        <Tooltip title={t('workflow.list.viewDetails')}>
          <Button
            {...iconBtn}
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/approver/workflows/${workflow.id}/versions/${row.versionNumber}`)
            }
          />
        </Tooltip>
      );
    }

    // "Create New Version" is per-row now: the Published row + every Archived row exposes
    // it. The action's offered only when the workflow is sitting on its Approved state
    // (no Draft/Pending/Rejected in flight) — only one editable in-flight version at a time.
    const noInFlight = workflow.status === 'Approved';
    const isPublishedRow = row.status === 'Approved' && row.isActive;
    const isArchivedRow = row.status === 'Approved' && !row.isActive;
    const canForkFromHere = noInFlight && (isPublishedRow || isArchivedRow);
    const renderForkButton = () => (
      <ConfirmPopover
        title={t('workflow.versions.newVersionConfirmTitle')}
        description={
          isPublishedRow
            ? t('workflow.versions.newVersionConfirmDescLive')
            : t('workflow.versions.newVersionConfirmDescFork', { label: row.versionLabel })
        }
        // Always pass the source row's versionNumber — even on Published — so the BE
        // records the lineage label and the "Based on" column has something to show.
        onConfirm={() => handleCreateNewVersion(row.versionNumber)}
      >
        <Tooltip title={t('workflow.versions.createNew')}>
          <Button
            {...iconBtn}
            type="primary"
            ghost
            icon={<PlusOutlined />}
            loading={busyRow === row.versionNumber || (isPublishedRow && busyRow === -1)}
          />
        </Tooltip>
      </ConfirmPopover>
    );

    // The Draft row created by "Create New Version" gets the full edit/delete/submit set.
    if (row.status === 'Draft') {
      return (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <Tooltip title={t('workflow.list.viewDetails')}>
            <Button
              {...iconBtn}
              icon={<EyeOutlined />}
              onClick={() => navigate(`/originator/workflows/${workflow.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('workflow.list.edit')}>
            <Button {...iconBtn} icon={<EditOutlined />} onClick={() => setEditOpen(true)} />
          </Tooltip>
          <ConfirmPopover
            title={t('workflow.versions.cancelNewConfirmTitle')}
            description={t('workflow.versions.cancelNewConfirmDesc')}
            onConfirm={() => handleDeleteDraft(row)}
          >
            <Tooltip title={t('workflow.list.delete')}>
              <Button {...iconBtn} danger icon={<DeleteOutlined />} />
            </Tooltip>
          </ConfirmPopover>
          {(() => {
            // Empty workflows + empty steps can't be submitted (BE refuses with a
            // DomainException; FE pre-disables for clarity). Tooltip swaps to the
            // reason so the user knows what to fix.
            const readiness = getSubmitReadiness(workflow, t);
            return (
              <ConfirmPopover
                title={t('workflow.list.sendConfirmTitle')}
                description={t('workflow.list.sendConfirmDesc')}
                onConfirm={() => handleSubmitDraft(row)}
                disabled={!readiness.ok}
              >
                <Tooltip
                  title={readiness.ok ? t('workflow.list.sendToApprover') : readiness.reason}
                >
                  <Button
                    {...iconBtn}
                    type="primary"
                    ghost
                    icon={<SendOutlined />}
                    disabled={!readiness.ok}
                    loading={busyRow === row.versionNumber}
                  />
                </Tooltip>
              </ConfirmPopover>
            );
          })()}
        </div>
      );
    }

    // Archived row (Approved + !isActive): two affordances on this row when no in-flight exists —
    //   - Restore (redo)   : in-place flip back to Pending → on approval becomes live again with
    //                        the old label (e.g., v1 stays v1). Audit kept.
    //   - Create New Version (+): fork content into a brand-new Draft (v(N+1)) the originator can
    //                             edit before submitting. Tagged "based on vN" in the table.
    if (isArchivedRow && canForkFromHere) {
      return (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <Tooltip title={t('workflow.list.viewDetails')}>
            <Button
              {...iconBtn}
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(`/originator/workflows/${workflow.id}/versions/${row.versionNumber}`)
              }
            />
          </Tooltip>
          <ConfirmPopover
            title={t('workflow.versions.activateConfirmTitle')}
            description={t('workflow.versions.activateConfirmDesc')}
            onConfirm={() => handleRestoreArchived(row)}
          >
            <Tooltip title={t('workflow.versions.restoreToLive')}>
              <Button
                {...iconBtn}
                type="primary"
                ghost
                icon={<RedoOutlined />}
                loading={busyRow === row.versionNumber}
              />
            </Tooltip>
          </ConfirmPopover>
          {renderForkButton()}
        </div>
      );
    }

    // Currently-Published row (Approved + isActive, no in-flight): the canonical "Create New
    // Version" entry point — used to be a global header button, now lives in this row's actions.
    if (isPublishedRow && canForkFromHere) {
      return (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <Tooltip title={t('workflow.list.viewDetails')}>
            <Button
              {...iconBtn}
              icon={<EyeOutlined />}
              onClick={() => navigate(`/originator/workflows/${workflow.id}`)}
            />
          </Tooltip>
          {renderForkButton()}
        </div>
      );
    }

    // Everything else (Pending, Rejected, Approved+inflight) — read-only details only.
    return (
      <Tooltip title={t('workflow.list.viewDetails')}>
        <Button
          {...iconBtn}
          icon={<EyeOutlined />}
          onClick={() => navigate(`/originator/workflows/${workflow.id}`)}
        />
      </Tooltip>
    );
  };

  const columns: ColumnsType<WorkflowVersionSummary> = [
    {
      title: t('workflow.versions.versionNumber'),
      dataIndex: 'versionLabel',
      key: 'versionLabel',
      width: 110,
      onHeaderCell: () => ({ style: { paddingLeft: 20 } }),
      onCell: () => ({ style: { paddingLeft: 20 } }),
      render: (label: string) => <Text strong>{label}</Text>,
    },
    {
      title: t('workflow.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      filters: [
        { text: t('workflow.statusLabels.Approved'), value: 'Published' },
        { text: t('workflow.versions.statusArchived'), value: 'Archived' },
        { text: t('workflow.statusLabels.Rejected'), value: 'Rejected' },
        { text: t('workflow.statusLabels.Pending'), value: 'Pending' },
        { text: t('workflow.statusLabels.Draft'), value: 'Draft' },
      ],
      defaultFilteredValue: ['Published', 'Draft', 'Pending', 'Archived'],
      onFilter: (value, row) => {
        const display =
          row.status === 'Approved' ? (row.isActive ? 'Published' : 'Archived') : row.status;
        return display === value;
      },
      render: (_: unknown, row: WorkflowVersionSummary) => (
        <StatusTag status={row.status} isActive={row.isActive} />
      ),
    },
    {
      title: t('workflow.versions.basedOnHeader'),
      dataIndex: 'forkedFromLabel',
      key: 'forkedFromLabel',
      width: 130,
      // Forked rows carry the source label; rows seeded from the live published row (the
      // default Create-New-Version path) don't get tagged, since the lineage is implicit.
      render: (forkedFromLabel?: string | null) =>
        forkedFromLabel ? <VersionChip label={forkedFromLabel} /> : <Text type="secondary">—</Text>,
    },
    {
      title: t('workflow.versions.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (iso: string) => new Date(iso).toLocaleString(i18n.language),
    },
    {
      title: t('workflow.versions.approvedBy'),
      key: 'approvedBy',
      // Display name with oid fallback; the row's terminal status decides which side renders.
      render: (_: unknown, row: WorkflowVersionSummary) =>
        row.status === 'Approved' ? (row.approvedByName ?? row.approvedBy ?? '—') : '—',
    },
    {
      // AC3 on "Store all previous versions" (2026-07-15): email must be
      // identifiable per version. Ships alongside the name column so an
      // approver-off-boarding or ambiguous-name case still resolves to a
      // real person.
      title: t('workflow.versions.approvedByEmail'),
      key: 'approvedByEmail',
      render: (_: unknown, row: WorkflowVersionSummary) =>
        row.status === 'Approved' && row.approvedByEmail ? row.approvedByEmail : '—',
    },
    {
      // AC3 on "Store all previous versions": each stored version must carry
      // its publish date. In the current controlled-rollout model this is
      // the approvedAt timestamp (approve = publish for BMO's read); the
      // flip-to-active moment carries its own separate audit trail.
      title: t('workflow.versions.approvedAt'),
      key: 'approvedAt',
      render: (_: unknown, row: WorkflowVersionSummary) =>
        row.status === 'Approved' && row.approvedAt
          ? new Date(row.approvedAt).toLocaleString(i18n.language)
          : '—',
    },
    {
      // AC3, second lane: name/email of whoever flipped the workflow live.
      // Distinct from Approved-by so an audit can distinguish content sign-off
      // from the go-live decision. Only rendered on rows whose lifetime
      // included a published moment — status=Approved (currently or
      // previously active) — so a Rejected row that still carries stale
      // approvedBy* from an earlier decision doesn't render a phantom
      // publisher. Legacy pre-2026-07-15 Approved rows fall back to Approver
      // info so the column is never empty on a workflow that IS live.
      title: t('workflow.versions.publishedBy'),
      key: 'publishedBy',
      render: (_: unknown, row: WorkflowVersionSummary) => {
        if (row.status !== 'Approved') return '—';
        const name = row.publishedByName ?? row.publishedBy ?? row.approvedByName ?? row.approvedBy;
        const email = row.publishedByEmail ?? row.approvedByEmail;
        if (!name && !email) return '—';
        return email ? `${name ?? '—'} <${email}>` : (name ?? '—');
      },
    },
    {
      title: t('workflow.versions.rejectedBy'),
      key: 'rejectedBy',
      render: (_: unknown, row: WorkflowVersionSummary) =>
        row.status === 'Rejected' ? (row.rejectedByName ?? row.rejectedBy ?? '—') : '—',
    },
    {
      title: t('workflow.versions.comment'),
      dataIndex: 'rejectionComment',
      key: 'rejectionComment',
      ellipsis: { showTitle: true },
      render: (v?: string | null) => v || '—',
    },
    {
      title: t('workflow.fields.actions'),
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_: unknown, row: WorkflowVersionSummary) => renderRowActions(row),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (!workflow) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={workflow.nameEn || workflow.appName}
        onBack={() => navigate(dashboardPath)}
      />

      <div style={OUTER_WRAP}>
        <div style={TOP_BAR}>
          {/* "Create New Version" used to live here; it's now a per-row action so the
              originator picks the source row explicitly (Published or any Archived row). */}
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {t('workflow.versions.title')}
          </h2>
        </div>

        {!versions || versions.length === 0 ? (
          <div style={{ padding: 48 }}>
            <Empty description={t('workflow.versions.empty')} />
          </div>
        ) : (
          <Table<WorkflowVersionSummary>
            rowKey="versionNumber"
            columns={columns}
            dataSource={versions}
            pagination={false}
            size="middle"
          />
        )}
      </div>

      <CreateWorkflowModal
        open={editOpen}
        editing={workflow}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setWorkflow(saved);
          void load();
        }}
      />
    </div>
  );
}
